import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, TrendingUp, MapPin, LogIn } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/cities`);
        setCities(response.data);
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = cities.filter(city =>
        city.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        city.state.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCities(filtered.slice(0, 8));
      setShowDropdown(filtered.length > 0);
    } else {
      setFilteredCities([]);
      setShowDropdown(false);
    }
  }, [searchQuery, cities]);

  const handleSearch = (location) => {
    if (location.trim()) {
      navigate(`/analysis?location=${encodeURIComponent(location)}`);
    }
  };

  const selectCity = (city) => {
    setSearchQuery(city.name);
    setShowDropdown(false);
    handleSearch(city.name);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100 h-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-full flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="font-display text-2xl font-bold text-slate-900 tracking-tight"
            data-testid="logo-button"
          >
            Atlas.
          </button>

          {!user && (
            <button
              onClick={() => navigate('/auth')}
              className="bg-slate-900 text-white rounded-lg px-6 py-2 font-medium hover:bg-slate-800 smooth-transition shadow-sm flex items-center gap-2"
              data-testid="nav-login-button"
            >
              <LogIn className="w-4 h-4" />
              Login / Register
            </button>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1
              className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-6"
              data-testid="hero-title"
            >
              Real Estate Intelligence
              <br />
              <span className="gradient-text">Simplified.</span>
            </h1>
            <p
              className="text-lg md:text-xl text-slate-600 mb-12 max-w-2xl mx-auto"
              data-testid="hero-subtitle"
            >
              Discover property insights, compare prices, and explore market trends across India with precision data analytics.
            </p>

            <div className="relative max-w-3xl mx-auto" data-testid="search-container">
              <div className="relative">
                <Search className="absolute left-8 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                  placeholder="Search by city, location, or pincode..."
                  className="h-16 text-lg px-20 rounded-full border-0 shadow-xl shadow-slate-200/50 bg-white/90 backdrop-blur-xl focus:ring-2 focus:ring-slate-900/5 smooth-transition placeholder:text-slate-400 w-full"
                  data-testid="search-input"
                />
                <button
                  onClick={() => handleSearch(searchQuery)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-slate-900 text-white rounded-full px-8 py-3 font-medium hover:bg-slate-800 smooth-transition shadow-sm"
                  data-testid="search-button"
                >
                  Analyze
                </button>
              </div>

              {showDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50"
                  data-testid="autocomplete-dropdown"
                >
                  {filteredCities.map((city, index) => (
                    <button
                      key={index}
                      onClick={() => selectCity(city)}
                      className="w-full px-6 py-4 text-left hover:bg-slate-50 smooth-transition flex items-center gap-3 border-b border-slate-100 last:border-0"
                      data-testid={`city-option-${index}`}
                    >
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <div>
                        <div className="font-medium text-slate-900">{city.name}</div>
                        <div className="text-sm text-slate-500">{city.state}</div>
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-3" data-testid="popular-cities">
              <span className="text-sm text-slate-500 font-medium">Popular:</span>
              {['Mumbai', 'Delhi', 'Bangalore', 'Pune', 'Hyderabad'].map((city) => (
                <button
                  key={city}
                  onClick={() => {
                    setSearchQuery(city);
                    handleSearch(city);
                  }}
                  className="text-sm px-4 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-900 hover:bg-slate-50 smooth-transition"
                  data-testid={`popular-city-${city.toLowerCase()}`}
                >
                  {city}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-xl p-8 hover:border-slate-300 smooth-transition"
            data-testid="feature-analysis"
          >
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-slate-900" />
            </div>
            <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
              Price Analysis
            </h3>
            <p className="text-slate-600">
              Get instant market insights with detailed price trends and comparative analytics.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-slate-200 rounded-xl p-8 hover:border-slate-300 smooth-transition"
            data-testid="feature-broker"
          >
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
              <Search className="w-6 h-6 text-slate-900" />
            </div>
            <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
              For Brokers
            </h3>
            <p className="text-slate-600">
              Access comprehensive property listings and make informed decisions for clients.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-slate-200 rounded-xl p-8 hover:border-slate-300 smooth-transition"
            data-testid="feature-company"
          >
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-slate-900" />
            </div>
            <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
              For Companies
            </h3>
            <p className="text-slate-600">
              List and manage your properties with ease. Reach the right audience instantly.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};